
import VisTileLarge from '@/components/vis/VisTileLarge.vue';
import LayoutGrid from '@/components/LayoutGrid.vue';
import {
  computed, ComputedRef, defineComponent, reactive, toRef, watch, watchEffect,
} from '@vue/composition-api';
import ScorePlot from './ScorePlot.vue';
import LoadingsPlot from './LoadingsPlot.vue';
import VipPlot from './VipPlot.vue';
import usePlotData from '../use/usePlotData';
import store from '../../../store';
import { downloadCSV } from '../../../utils/exporter';

export default defineComponent({
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  components: {
    ScorePlot,
    LoadingsPlot,
    VipPlot,
    VisTileLarge,
    LayoutGrid,
  },
  setup(props) {
    const { dataset, plot, changePlotArgs } = usePlotData(toRef(props, 'id'), 'plsda');
    const controls = reactive({
      pcXval: '1',
      pcYval: '2',
      numComponentsVal: '3',
      pcX: 1,
      pcY: 2,
      numComponents: 3,
      showEllipses: true,
      showCrosshairs: true,
      showCutoffs: true,
      showScore: true,
      showLoadings: true,
      showVip: true,
      vipComponentVal: '1',
      vipComponent: 1,
      sortVip: false,
    });

    const ready = computed(() => {
      const pcaReady = store.getters.ready(props.id, 'plsda_scores');
      const loadingsReady = store.getters.ready(props.id, 'plsda_loadings');
      return pcaReady && loadingsReady;
    });
    const r2: ComputedRef<number[]> = computed(() => plot.value.data?.r2 || []);
    const q2: ComputedRef<number[]> = computed(() => plot.value.data?.q2 || []);
    const r2q2Table = computed(() => r2.value.map((r2Val, i) => ({ name: `PC${i + 1}`, r2: r2Val.toFixed(3), q2: q2.value[i].toFixed(3) })));
    const r2cum = computed(() => r2.value.reduce((a, b) => a + b, 0).toFixed(3));
    const q2cum = computed(() => q2.value.reduce((a, b) => a + b, 0).toFixed(3));
    const loadings = computed(() => plot.value.data?.loadings || []);
    const allVipScores = computed(() => plot.value.data?.vip_scores || []);
    const sortedVipScores = computed((): { col: string; vip: number }[] => {
      const selectedVipScores = allVipScores.value[controls.vipComponent - 1];
      if (!selectedVipScores) {
        return [];
      }
      if (controls.sortVip) {
        const copy = [...selectedVipScores];
        copy.sort((a, b) => b.vip - a.vip);
        return copy;
      }
      return selectedVipScores;
    });
    const pcCoords = computed(() => plot.value.data?.scores.x || []);
    const eigenvalues = computed(() => plot.value.data?.scores.sdev || []);
    const rowLabels = computed(() => plot.value.data?.rows || []);
    const groupLabels = computed(() => plot.value.data?.labels || {});
    const columns = computed(() => dataset.value?.column.data || []);
    const groupLevels = computed(() => dataset.value?.groupLevels || []);

    watchEffect(() => {
      const pcX = Number.parseInt(controls.pcXval, 10);
      if (!Number.isNaN(pcX) && pcX <= controls.numComponents) {
        controls.pcX = pcX;
      }
    });
    watchEffect(() => {
      const pcY = Number.parseInt(controls.pcYval, 10);
      if (!Number.isNaN(pcY) && pcY <= controls.numComponents) {
        controls.pcY = pcY;
      }
    });
    watch(() => controls.numComponentsVal, () => {
      const numComponents = Number.parseInt(controls.numComponentsVal, 10);
      if (!Number.isNaN(numComponents)) {
        controls.numComponents = numComponents;
        if (plot.value) {
          plot.value.valid = false;
        }
        changePlotArgs({ num_of_components: controls.numComponents });
      }
    });
    watchEffect(() => {
      const vipComponent = Number.parseInt(controls.vipComponentVal, 10);
      if (!Number.isNaN(vipComponent) && vipComponent <= controls.numComponents) {
        controls.vipComponent = vipComponent;
      }
    });

    function downloadVipScores() {
      const rows: string[] = sortedVipScores.value.map((v) => `${v.col},${v.vip}`);
      downloadCSV(`Metabolite,VIP_Score\n${rows.join('\n')}`, `${dataset.value.name}_PLSDA_VIP_Scores`);
    }

    return {
      plot,
      changePlotArgs,
      controls,
      ready,
      loadings,
      sortedVipScores,
      r2q2Table,
      r2cum,
      q2cum,
      pcCoords,
      eigenvalues,
      rowLabels,
      groupLabels,
      columns,
      groupLevels,
      downloadVipScores,
    };
  },
});
